import clsx from 'clsx';
import type { ComponentProps } from 'react';

import { isExternalURL } from '@commons/helpers/url';
import { Link as NextLink } from '@commons/i18n/navigation';

import Icon from '../Icon';
import Text, { type TextProps } from '../Text';
import styles from './index.module.scss';

// Type definition for the Link arrow icon sizes.

type LinkArrowSize = 'sm' | 'md' | 'lg';

/**
 * Link component props.
 *
 * Based on Next.js Link component.
 */

export type LinkProps = ComponentProps<typeof NextLink> & {
  level?: TextProps['level'];
  weight?: TextProps['weight'];
  active?: boolean;
  underline?: boolean;
  reverse?: boolean;
  transform?: TextProps['transform'];
  withArrow?: boolean | LinkArrowSize;
};

/**
 * Link component.
 *
 * Wraps Next.js Link component and adds some extra features.
 */

export const Link = (props: LinkProps) => {
  const {
    href,
    children,
    target,
    level,
    weight,
    active = false,
    transform,
    underline,
    reverse,
    withArrow,
    className,
    style,
    ref,
    ...rest
  } = props;

  // Checking the href value to determine if the link is external.
  const isExternal = isExternalURL(href);

  const classes = clsx(
    'link',
    styles.link,
    active && ['active', styles.active],
    underline && !withArrow && ['underline', styles.underline],
    reverse && !withArrow && ['reverse', styles.reverse],
    withArrow && styles.withArrow,
    isExternal && styles.isExternal,
    withArrow && typeof withArrow === 'string' && styles[`withArrow-size-${withArrow}`],
    className
  );

  const renderedArrow = withArrow && (
    <Icon type={isExternal ? 'external' : 'arrowRight'} className={styles.arrow} />
  );

  return (
    <NextLink
      ref={ref}
      href={href || '#'}
      target={target || (isExternal ? '_blank' : undefined)}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      className={classes}
      style={style}
      {...rest}
    >
      <Text
        as="span"
        level={level}
        weight={weight}
        transform={transform}
        className={styles.label}
        marginBottom="none"
      >
        {children}
      </Text>
      {renderedArrow}
    </NextLink>
  );
};

export default Link;
